import { useState } from 'react';
import { Button, Card, CardBody, Col, Table } from 'reactstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiChevronLeft } from '@mdi/js';
import claimNoIcon from '../../../../assets/icons/claimnoicon.png';
import claimDateIcon from '../../../../assets/icons/claimdateicon.png';
import advPaidIcon from '../../../../assets/icons/advpaidicon.png';
import downloadExcelIcon from '../../../../assets/icons/downloadexcelicon.png';
import printIcon from '../../../../assets/icons/printicon.png';
import {
  fetchAllCountryCurrency,
  saveExpenseClaim,
} from '../../../../store/apis/expenseService';
import { useExpenseClaimCtx } from '../../context/ExpenseClaimProvider';
import { useExpenseClaimUtilCtx } from '../../context/ExpenseClaimUtilProvider';
import {
  ModalWrapper,
  ModalTable,
  ModalMultiWrapper,
  SpinnerLoading,
  DisplayModal,
} from '../../../../components/Common';
import { draftDataConfig } from './constants';
import {
  CancelExpenseClaimModal,
  UploadAttachmentsModal,
  ViewAttachmentsModal,
  PrintModal,
  ExpenseClaimRow,
  AdvanceRecoveryModal,
} from '../../../../components/ExpenseClaims';
import { FileUtil, DateUtil, NumberUtil } from '../../../../utils';
import { approveRejectRequest } from '../../../../store/apis/approvalService';

import './EmployeeExpenseClaims.scss';
import { withBase } from '../../../../utils/route.util';

function EmployeeExpenseClaims() {
  document.title = 'Expense Claims | Daikin Envision';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    formMethods: methods,
    formFieldArray,
    addRow,
    expenseClaimHeaders,
    isModalOpen,
    setIsModalOpen,
    modalConfig,
    setModalConfig,
    resetExpenseClaimAll,
    viewAttachmentsModalOpen,
    viewAttachmentSrNo,
    setViewAttachmentsModalOpen,
    modeType,
    approvalDetails,
    runValidationsAndToastErrors,
    isLoadingAll,
    displayModalOpen,
    displayModalFile,
    setDisplayModalOpen,
  } = useExpenseClaimCtx();
  const {
    fetchAttachmentsMutation,
    submitExpenseClaimMutation,
    validateExpenseClaimMutation,
    printExpenseClaimMutation,
    deleteExpenseClaimAttachmentListMutation,
    cancelExpenseClaimMutation,
    printExpenseClaimAttachmentMutation,
    getExpenseClaimDetailsMutation,
    defaultCurrCode,
  } = useExpenseClaimUtilCtx();
  const [searchText, setSearchText] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [multiModalOpen, setMultiModalOpen] = useState(false);
  const [multiModelName, setMultiModelName] = useState('');
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [uploadAttachmentsModalOpen, setUploadAttachmentsModalOpen] =
    useState(false);
  const [advanceRecoveryModalOpen, setAdvanceRecoveryModalOpen] =
    useState(false);

  const { watch } = methods;
  const { fields, append, remove } = formFieldArray;
  const { data: currencyLists } = useQuery({
    queryKey: ['currencyLists', searchText],
    queryFn: () =>
      fetchAllCountryCurrency({
        prefixText: searchText,
        type: 'HR_CURRENCY',
        param1: '',
        param2: '',
      }),
  });

  // Handle Approve Expense Claim
  const approveRejectRequestMutation = useMutation({
    mutationFn: approveRejectRequest,
  });

  // save expense claim
  const saveExpenseClaimMutation = useMutation({
    mutationFn: payload => saveExpenseClaim(payload),
  });

  const generatePayload = values => {
    const expenseDetails = values.claims.map((row, idx) => ({
      ...row,
      srNo: idx + 1,
    }));

    // Construct full payload
    const payload = {
      ...expenseClaimHeaders,
      expenseDetails,
    };
    return payload;
  };

  const handleSave = async values => {
    const payload = generatePayload(values);
    const response = await saveExpenseClaimMutation.mutateAsync(payload);
    if (response?.success && response?.success === true) {
      toast.success(
        `Expense Claim No #${response?.claimNo} Saved Successfully!`,
        {
          className: 'toast-success',
        },
      );
      queryClient.invalidateQueries(['expenseClaimDetailsHeader']);
      queryClient.invalidateQueries(['expenseClaimDetails']);
      navigate(`/expense/claim?ID=${response?.claimNo}`);
    } else {
      toast.error(
        response?.message || 'Failed to save expense claim. Please try again.',
        {
          className: 'toast-error',
        },
      );
    }
  };

  // Handle open draft expense claim modal
  const handleOpenDraft = async () => {
    const response = await getExpenseClaimDetailsMutation.mutateAsync({
      listType: 'DRAFT',
    });
    setModalConfig({
      title: 'Claims - Draft',
      data: response,
      dataConfig: draftDataConfig({
        onClose: () => setIsModalOpen(false),
      }),
    });
    setIsModalOpen(true);
  };

  const { errors: formErrors } = methods.formState;

  const handleAddRow = () => {
    if (!methods?.formState?.isValid) {
      toast.error(
        'Please fill in all required fields before adding a new row.',
        {
          className: 'toast-error',
        },
      );
      return;
    }
    addRow();
  };

  const renderTableBody = () =>
    fields.map((item, index) => (
      <ExpenseClaimRow
        key={item.id}
        index={index}
        item={item}
        remove={remove}
        currencyLists={currencyLists}
        defaultCurrCode={defaultCurrCode}
        setSearchText={setSearchText}
        methods={methods}
        watch={watch}
        append={append}
        totalRows={fields.length}
        formType={(modeType === 'approve' || modeType === 'view') && 'view'}
        errorsExist={formErrors?.claims?.[index] && true}
        handleAddRow={handleAddRow}
      />
    ));

  const handleAttachments = async () => {
    setUploadAttachmentsModalOpen(true);
  };

  const handleSubmitExpenseClaim = async values => {
    try {
      setSubmitLoading(true);
      // Get all document list
      const documentList = await fetchAttachmentsMutation.mutateAsync({
        docType: 'DOC_TYPE_EXPENSE_CLAIM',
        claimNo: expenseClaimHeaders?.claimNo || '',
        finCoCd: expenseClaimHeaders?.finCoCd || '',
      });

      if (documentList.length === 0) {
        toast.error(
          'Claim Evidence is mandatory. Please attach the necessary proof.',
          {
            className: 'toast-error',
          },
        );
        setSubmitLoading(false);
        return;
      }

      const payload = generatePayload(values);
      const response = await saveExpenseClaimMutation.mutateAsync(payload);
      if (response?.success && response?.success === true) {
        const submitExpenseClaimResponse =
          await submitExpenseClaimMutation.mutateAsync({
            claimNo: response?.claimNo,
            claimType: 'S',
          });
        if (
          submitExpenseClaimResponse?.flag &&
          submitExpenseClaimResponse?.flag === 'True' &&
          submitExpenseClaimResponse?.claimNo
        ) {
          toast.success(
            `Claim (${submitExpenseClaimResponse?.claimNo}) saved successfully.Please submit the claim form along with original to finance for processing the claim.`,
            {
              className: 'toast-success',
            },
          );
          await new Promise(resolve => {
            setTimeout(resolve, 1500);
          });
          queryClient.invalidateQueries(['expenseClaimDetailsHeader']);
          queryClient.invalidateQueries(['expenseClaimDetails']);
          window.location.href = withBase('/expense/claim');
          window.location.replace(withBase('/expense/claim'));
          setSubmitLoading(false);
        }

        if (
          submitExpenseClaimResponse?.flag &&
          submitExpenseClaimResponse?.flag === 'False'
        ) {
          // Show error modal
          setModalConfig({
            title: `Expense Claim ${expenseClaimHeaders?.claimNo} submission failed due to the error(s) below:`,
            data: submitExpenseClaimResponse?.errorData, // array of error objects
            dataConfig: null, // column config
          });
          setIsModalOpen(true);
          setSubmitLoading(false);
        }
      }
    } catch (error) {
      const errorMessage =
        error?.err?.meta?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred';
      toast.error(errorMessage, {
        className: 'toast-error',
      });
      setSubmitLoading(false);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!expenseClaimHeaders?.claimNo) {
      toast.error('No claim number found to print.', {
        className: 'toast-error',
      });
      return;
    }
    const response = await validateExpenseClaimMutation.mutateAsync({
      claimNo: expenseClaimHeaders?.claimNo,
      claimType: 'P',
    });

    if (response?.flag === 'True') {
      await printExpenseClaimMutation.mutateAsync({
        claimNo: expenseClaimHeaders?.claimNo,
      });
      toast.success(
        `Claim (${expenseClaimHeaders?.claimNo}) is ready for printing.`,
        {
          className: 'toast-success',
        },
      );
      setIsPrintModalOpen(true);
      return;
    }
    if (response?.flag === 'False') {
      setModalConfig({
        title: `Expense Claim ${expenseClaimHeaders?.claimNo} printing failed due to error specified below:`,
        data: response?.errorData,
        dataConfig: null,
      });
      setIsModalOpen(true);
    }
  };

  const handlePrintExcel = async () => {
    if (!expenseClaimHeaders?.claimNo) {
      toast.error('No claim number found to download as Excel.', {
        className: 'toast-error',
      });
      return;
    }
    try {
      const printResponseBlob =
        await printExpenseClaimAttachmentMutation.mutateAsync({
          docType: 'DOC_TYPE_EXPENSE_CLAIM',
          claimNo: expenseClaimHeaders?.claimNo || '',
          type: 'EXCEL',
        });
      FileUtil.downloadBlobFile(
        printResponseBlob,
        `Expense_Claim_${expenseClaimHeaders?.claimNo}.xlsx`,
      );
    } catch (error) {
      toast.error(
        error?.message || 'Failed to download expense claim as Excel.',
        {
          className: 'toast-error',
        },
      );
    }
  };

  const handleCancelExpenseClaim = async payload => {
    await deleteExpenseClaimAttachmentListMutation.mutateAsync({
      docType: 'DOC_TYPE_EXPENSE_CLAIM',
      claimNo: expenseClaimHeaders?.claimNo || '',
      srNo: 0,
    });
    // Cancel the expense claim
    const response = await cancelExpenseClaimMutation.mutateAsync({
      claimNo: expenseClaimHeaders?.claimNo || '',
      reason: payload.cancelReason,
      srNo: 0,
    });
    if (response?.success === true) {
      toast.success(
        `Expense Claim (${expenseClaimHeaders?.claimNo}) cancelled successfully.`,
        {
          className: 'toast-success',
        },
      );
      setMultiModalOpen(false);
      navigate('/expense/claim', { replace: true });
      resetExpenseClaimAll();
    } else {
      toast.error(
        response?.errorData ||
          'Failed to cancel expense claim. Please try again.',
        {
          className: 'toast-error',
        },
      );
    }
  };

  const handleApproveExpenseClaim = async () => {
    if (!approvalDetails) {
      toast.error('Approval details are not available.', {
        className: 'toast-error',
      });
      return;
    }
    try {
      await approveRejectRequestMutation.mutateAsync({
        actionType: 'A',
        requestId: expenseClaimHeaders?.claimNo,
        requestedBy: expenseClaimHeaders?.RequestedBy,
        category: 'Expense Claim',
        typ: approvalDetails?.approvalType,
        approvalRank: approvalDetails?.approvalRank || 0,
        requestedDate: approvalDetails?.requestedDate,
        requestType: 'Expense Claim',
        details: approvalDetails?.details,
        requestorId: approvalDetails?.requestorId,
        coCd: approvalDetails?.companyCode,
      });
      toast.success(
        `Expense Claim for (${expenseClaimHeaders?.requestedBy}) has been approved successfully.`,
        {
          className: 'toast-success',
        },
      );
      navigate('/approvals');
    } catch (error) {
      const errorMessage =
        error?.err?.meta?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred';
      toast.error(errorMessage, {
        className: 'toast-error',
      });
    }
  };

  if (isLoadingAll) {
    return <SpinnerLoading spinnerSize="2rem" height="100vh" />;
  }

  return (
    <FormProvider {...methods}>
      <div className="page-content expense-claim-page">
        <Card className="eec-card m-0">
          <CardBody className="p-0">
            {/* Header */}
            <div className="eec-header">
              {/* Expense Claim Header */}
              <div className="eec-header-title px-2 d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  {modeType === 'approve' || modeType === 'view' ? (
                    <>
                      <button
                        type="button"
                        className="p-0 btn btn-link trd-back d-flex align-items-center"
                        onClick={() => navigate(-1)}
                      >
                        <Icon
                          path={mdiChevronLeft}
                          className="icon-chevron-left"
                          size={1.1}
                        />
                        <span className="back fs-5">Back</span>
                      </button>
                      <div
                        style={{
                          borderLeft: '1px solid lightgrey',
                          height: '30px',
                        }}
                      />
                      <span className="fw-semibold fs-5">Expense Claim</span>
                    </>
                  ) : (
                    <span className="fw-semibold fs-5 px-2">Expense Claim</span>
                  )}
                </div>
              </div>
              {/* Claim Card Info */}
              <div className="d-flex gap-2 p-3 flex-row align-items-center justify-content-between">
                <Col>
                  <div className="claim-info-card">
                    <div className="claim-icon">
                      <img
                        src={claimNoIcon}
                        alt="Claim Number Icon"
                        className="claim-icon-img"
                      />
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <div
                        className="claim-title"
                        title="Auto-generated unique number assigned to this expense claim."
                      >
                        Claim Number
                      </div>
                      <div title="Auto-generated unique number assigned to this expense claim.">
                        {expenseClaimHeaders?.claimNo || '--'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="claim-info-card">
                    <div className="claim-icon">
                      <img
                        src={claimDateIcon}
                        alt="Claim Date Icon"
                        className="claim-icon-img"
                      />
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <div
                        className="claim-title"
                        title="Date when the expense claim is being submitted."
                      >
                        Claim Date
                      </div>
                      <div title="Date when the expense claim is being submitted.">
                        {expenseClaimHeaders?.claimDate
                          ? DateUtil.isoToDdMmYyyySlice(
                              expenseClaimHeaders?.claimDate,
                            )
                          : '--'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="claim-info-card">
                    <div className="claim-icon">
                      <img
                        src={claimNoIcon}
                        alt="Claim Number Icon"
                        className="claim-icon-img"
                      />
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <div
                        className="claim-title"
                        title="Auto-generated unique number assigned to this expense claim."
                      >
                        Requested By
                      </div>
                      <div title="Auto-generated unique number assigned to this expense claim.">
                        {expenseClaimHeaders?.requestedBy || '--'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="claim-info-card">
                    <div className="claim-icon">
                      <img
                        src={advPaidIcon}
                        alt="Adv Paid Icon"
                        className="claim-icon-img"
                      />
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <div
                        className="claim-title"
                        title="Shows the total amount of the expense claim."
                      >
                        {`Expense Total (${expenseClaimHeaders?.currency || 'Currency'})`}
                      </div>
                      <div>
                        {NumberUtil.truncateNumber(
                          expenseClaimHeaders?.claimAmt || 0,
                          2,
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                </Col>
              </div>
            </div>
            {/* Body + Table Form */}
            <div className="table-responsive eec-table-container">
              <Table className="">
                <thead>
                  <tr>
                    <th className="fw-semibold">Category</th>
                    <th className="fw-semibold">Type</th>
                    <th className="fw-semibold">Bill Date</th>
                    <th className="fw-semibold">Curr.</th>
                    <th className="fw-semibold">Ex.Rate</th>
                    <th className="fw-semibold">Amount</th>
                    <th className="fw-semibold">VAT</th>
                    <th className="fw-semibold">Remarks</th>
                    <th className="fw-semibold">Attachment</th>
                    <th className="fw-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="eec-table-body">{renderTableBody()}</tbody>
              </Table>
            </div>
            {/* Footer */}
            <div className="eec-footer p-3">
              <div className="eec-footer-btns">
                <div className="eec-footer-btns-left">
                  <Button
                    type="button"
                    className="custom-light-btn download-btn"
                    onClick={handlePrintExcel}
                  >
                    Download as Excel
                    <img
                      src={downloadExcelIcon}
                      className="eec-footer-icon"
                      alt="Download as Excel"
                    />
                  </Button>
                  <Button
                    type="button"
                    className="custom-light-btn print-btn"
                    onClick={handlePrint}
                    disabled={modeType === 'approve' || modeType === 'view'}
                    style={{
                      cursor:
                        modeType === 'approve' || modeType === 'view'
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        modeType === 'approve' || modeType === 'view' ? 0.5 : 1,
                    }}
                  >
                    Print
                    <img
                      src={printIcon}
                      className="eec-footer-icon"
                      alt="Print"
                    />
                  </Button>
                </div>
                <div className="eec-footer-btns-right">
                  {modeType === 'approve' && (
                    <Button
                      type="button"
                      className="custom-success-btn"
                      onClick={handleApproveExpenseClaim}
                    >
                      Approve
                    </Button>
                  )}
                  {modeType === 'create' && (
                    <>
                      <Button
                        type="button"
                        className="custom-light-btn"
                        onClick={() => {
                          setMultiModelName('cancelExpenseClaimModal');
                          setMultiModalOpen(true);
                        }}
                        disabled={!expenseClaimHeaders?.claimNo}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        className="custom-light-btn"
                        onClick={handleOpenDraft}
                      >
                        Draft
                      </Button>
                      <Button
                        type="button"
                        className="custom-secondary-btn"
                        onClick={() => {
                          runValidationsAndToastErrors();
                          methods.handleSubmit(handleSave)();
                        }}
                        style={{
                          cursor: methods.formState?.isValid
                            ? 'pointer'
                            : 'not-allowed',
                          opacity: methods.formState?.isValid ? 1 : 0.5,
                        }}
                      >
                        Save
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    className="custom-light-btn"
                    onClick={handleAttachments}
                    disabled={!expenseClaimHeaders?.claimNo}
                  >
                    Attachment
                  </Button>
                  {modeType === 'create' && (
                    <Button
                      type="button"
                      className="custom-btn"
                      onClick={() =>
                        methods.handleSubmit(handleSubmitExpenseClaim)()
                      }
                      disabled={!expenseClaimHeaders?.claimNo}
                    >
                      {submitLoading ? 'Submitting...' : 'Submit'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
      <ModalWrapper
        isModalOpen={isModalOpen}
        toggle={() => setIsModalOpen(!isModalOpen)}
        title={modalConfig.title}
        onButtonClick={() => setIsModalOpen(false)}
        buttonText="Close"
        size="xl"
      >
        <ModalTable
          data={modalConfig.data}
          dataConfig={modalConfig.dataConfig}
          index
        />
      </ModalWrapper>
      <ModalMultiWrapper
        modals={{
          cancelExpenseClaimModal: {
            component: CancelExpenseClaimModal,
            props: {
              handleOnSubmit: handleCancelExpenseClaim,
              isLoading: cancelExpenseClaimMutation?.isPending,
            },
          },
        }}
        isOpen={multiModalOpen}
        toggle={() => setMultiModalOpen(false)}
        selectedModal={multiModelName}
      />
      <PrintModal
        isOpen={isPrintModalOpen}
        toggle={() => setIsPrintModalOpen(!isPrintModalOpen)}
        claimNo={expenseClaimHeaders?.claimNo}
      />
      {uploadAttachmentsModalOpen && (
        <UploadAttachmentsModal
          isOpen={uploadAttachmentsModalOpen}
          toggle={() =>
            setUploadAttachmentsModalOpen(!uploadAttachmentsModalOpen)
          }
          claimNo={expenseClaimHeaders?.claimNo || ''}
          claimStatus={expenseClaimHeaders?.claimStatus}
          formType={(modeType === 'approve' || modeType === 'view') && 'view'}
          finCoCd={expenseClaimHeaders?.finCoCd}
        />
      )}
      {viewAttachmentsModalOpen && (
        <ViewAttachmentsModal
          isOpen={viewAttachmentsModalOpen}
          toggle={() => setViewAttachmentsModalOpen(false)}
          claimNo={expenseClaimHeaders?.claimNo}
          srNo={viewAttachmentSrNo}
          claimStatus={expenseClaimHeaders?.claimStatus}
          finCoCd={expenseClaimHeaders?.finCoCd}
        />
      )}
      <AdvanceRecoveryModal
        isOpen={advanceRecoveryModalOpen}
        toggle={() => setAdvanceRecoveryModalOpen(false)}
        claimNo={expenseClaimHeaders?.claimNo || ''}
        formType={(modeType === 'approve' || modeType === 'view') && 'view'}
        employeeId={expenseClaimHeaders?.employeeId}
      />
      <DisplayModal
        isModalOpen={displayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
    </FormProvider>
  );
}

export default EmployeeExpenseClaims;
